import React, { useEffect, useState } from "react";
import { api, fmtDate, BACKEND_URL } from "../lib/api";
import { PageHeader, Surface, Btn, Field, Input, Select } from "../components/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { Plus, X, FileArrowDown, Trash } from "@phosphor-icons/react";

const CATEGORIES = ["license", "medical", "insurance", "airworthiness_certificate", "registration_certificate", "noise_certificate", "arc", "camo", "radio_license", "elt_registration", "maintenance_release", "other"];

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "license", entity_type: "self", entity_id: "", expiry_date: "", file: null });

  const load = () => Promise.all([
    api.get("/documents").then((r) => setDocs(r.data)),
    api.get("/aircraft").then((r) => setAircraft(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.file) return;
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("category", form.category);
    const eid = form.entity_type === "self" ? user.user_id : form.entity_id;
    fd.append("entity_id", eid);
    if (form.expiry_date) fd.append("expiry_date", form.expiry_date);
    await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setOpen(false);
    setForm({ category: "license", entity_type: "self", entity_id: "", expiry_date: "", file: null });
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  const today = new Date().toISOString().substring(0, 10);
  const isExpired = (d) => d && d < today;
  const isSoon = (d) => d && !isExpired(d) && new Date(d) - new Date() < 30 * 86400000;

  return (
    <div>
      <PageHeader overline="Compliance" title="Documents"
        subtitle="License, medical, insurance, ARC, CAMO and other expiry-tracked artefacts."
        actions={<Btn data-testid="upload-doc-btn" onClick={() => setOpen(true)}><Plus size={14} className="inline mr-1.5" />Upload</Btn>}
      />
      <div className="p-8">
        <Surface>
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              <tr>
                <th className="text-left py-3 px-4">Category</th><th className="text-left py-3 px-4">File</th>
                <th className="text-left py-3 px-4">Uploaded by</th><th className="text-left py-3 px-4">Uploaded</th>
                <th className="text-left py-3 px-4">Expires</th><th className="text-right py-3 px-4 w-32">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="documents-table">
              {docs.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-zinc-500">No documents uploaded yet.</td></tr>}
              {docs.map((d) => (
                <tr key={d.id} className="border-b border-zinc-900 hover:bg-[#1F1F1F]">
                  <td className="py-2.5 px-4 capitalize">{d.category.replace("_", " ")}</td>
                  <td className="py-2.5 px-4 font-mono text-xs text-zinc-300 truncate max-w-xs">{d.original_filename}</td>
                  <td className="py-2.5 px-4 text-zinc-400">{d.uploaded_by_name}</td>
                  <td className="py-2.5 px-4 font-mono text-zinc-400">{fmtDate(d.created_at)}</td>
                  <td className="py-2.5 px-4 font-mono">
                    {d.expiry_date ? (
                      <span className={isExpired(d.expiry_date) ? "text-red-400" : isSoon(d.expiry_date) ? "text-amber" : "text-zinc-300"}>
                        {d.expiry_date}{isExpired(d.expiry_date) && " · expired"}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <a href={`${BACKEND_URL}/api/files/${d.storage_path}`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber mr-3" data-testid={`download-${d.id}`}>
                      <FileArrowDown size={14} />
                    </a>
                    <button onClick={() => remove(d.id)} className="text-zinc-500 hover:text-red-400" data-testid={`delete-doc-${d.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="upload-modal">
          <Surface className="w-full max-w-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium">Upload document</h3>
              <button onClick={() => setOpen(false)}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Category">
                <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Belongs to">
                <Select value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value, entity_id: "" })}>
                  <option value="self">My profile</option>
                  <option value="aircraft">Aircraft</option>
                </Select>
              </Field>
              {form.entity_type === "aircraft" && (
                <Field label="Aircraft">
                  <Select value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })}>
                    <option value="">Select…</option>
                    {aircraft.map((a) => <option key={a.id} value={a.id}>{a.registration}</option>)}
                  </Select>
                </Field>
              )}
              <Field label="Expiry date (optional)"><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
              <Field label="File">
                <input type="file" data-testid="upload-file-input" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} className="text-sm text-zinc-300 file:bg-amber file:text-black file:border-0 file:px-3 file:py-1.5 file:mr-3 file:font-medium file:text-xs file:uppercase file:tracking-wider" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={save} data-testid="upload-save-btn">Upload</Btn>
            </div>
          </Surface>
        </div>
      )}
    </div>
  );
}
